/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { WizardPage } from "./@types/WizardData";

export type NavigationMenuType = {
  label: string;
  value: string;
  hidden?: boolean;
  requiresAdmin?: boolean;
};
export const navigationMenu: NavigationMenuType[] = [
  { label: "Dashboard", value: "dashboard" },
  { label: "Live monitor", value: "live" },
  { label: "Transfers", value: "transfers" },
  { label: "Deployments", value: "deployments" },
  { label: "Change control", value: "cutovers" },
  { label: "Disaster recovery", value: "dr" },
  { label: "Cloud endpoints", value: "endpoints" },
  { label: "SwiftIQ Agents", value: "minion-pools" },
  { label: "On-prem servers", value: "bare-metal-servers" },

  // Optional pages
  { label: "Planning", value: "planning" },

  // User management pages
  { label: "Projects", value: "projects", requiresAdmin: true },
  { label: "Users", value: "users", requiresAdmin: true },
  { label: "Logs", value: "logging", requiresAdmin: true },
  { label: "Audit history", value: "audit", requiresAdmin: true },
];

// https://github.com/cloudbase/coriolis/blob/master/coriolis/constants.py
export const providerTypes = {
  TARGET_TRANSFER: 4,
  SOURCE_TRANSFER: 8,
  CONNECTION: 16,
  DESTINATION_OPTIONS: 512,
  SOURCE_OPTIONS: 131072,
  STORAGE: 32768,
  SOURCE_UPDATE: 65536,
  TARGET_UPDATE: 262144,
  SOURCE_MINION_POOL: 524288,
  DESTINATION_MINION_POOL: 1048576,
  INVENTORY_EXPORT: 2097152,
};

export const loginButtons = [
  // {
  //   name: 'Google',
  //   id: 'google',
  //   url: '',
  // },
];

export const executionOptions = [
  {
    name: "shutdown_instances",
    type: "boolean",
    label: "Shut down source VMs",
    defaultValue: false,
    nullableBoolean: false,
    description:
      "When enabling this option, SwiftIQ will attempt to gracefully shut down the source instance(s), before syncing their data. This is recommended when executing Migrations, right before planning on completely Deploying on the destination cloud, as it will transfer the last chunks of data after the instance(s) have been shut down, and it leaves the source instance(s) off.",
  },
  {
    name: "auto_deploy",
    type: "boolean",
    label: "Auto Deploy",
    defaultValue: false,
    nullableBoolean: false,
    description:
      "When enabled, the transfer will automatically deploy the instances on the destination cloud after the transfer is complete.",
  },
];

export const executeOptionsWithExecuteNow = [
  ...executionOptions,
  {
    name: "execute_now",
    type: "boolean",
    label: "Execute Now",
    defaultValue: true,
    nullableBoolean: false,
    description:
      "When enabled, the transfer will be executed immediately after the options are configured.",
  },
];

export const deploymentFields = [
  {
    name: "clone_disks",
    type: "boolean",
    label: "Clone disks",
    defaultValue: false,
    nullableBoolean: false,
    description:
      "Copy the replica's disks into the new VM, keeping the replica for later syncs and test cut-overs. Off: the disks are handed to the new VM without a copy, and the next sync is a full one.",
  },
  {
    name: "skip_os_morphing",
    type: "boolean",
    label: "Skip OS morphing",
    defaultValue: false,
    nullableBoolean: false,
    description:
      "When enabled, OS morphing will be skipped during the deployment.",
  },
];

export const wizardPages: WizardPage[] = [
  {
    id: "type",
    title: "Transfer type",
    breadcrumb: "Type",
    description: "How the virtual machines move: once, or kept in sync for disaster recovery.",
  },
  {
    id: "source",
    title: "Source cloud",
    breadcrumb: "Source cloud",
    description: "Where the virtual machines run today.",
  },
  {
    id: "source-options",
    title: "Source options",
    breadcrumb: "Source options",
    description: "How SwiftIQ reads from the source cloud, and how much bandwidth it may use.",
  },
  {
    id: "vms",
    title: "Virtual machines",
    breadcrumb: "Virtual machines",
    description: "The virtual machines to move.",
  },
  {
    id: "target",
    title: "Target cloud",
    breadcrumb: "Target cloud",
    description: "Where the virtual machines will run.",
  },
  {
    id: "dest-options",
    title: "Target options",
    breadcrumb: "Target options",
    description: "Size, placement and settings for the new virtual machines.",
  },
  {
    id: "prerequisites",
    title: "Target prerequisites",
    breadcrumb: "Prerequisites",
    description: "What the target cloud needs before the first copy.",
  },
  {
    id: "networks",
    title: "Networks",
    breadcrumb: "Networks",
    description: "Which target network each source network connects to.",
  },
  {
    id: "storage",
    title: "Storage",
    breadcrumb: "Storage",
    description: "Which target storage each disk is written to.",
  },
  // Guest access before readiness: readiness then runs with every choice made, and the guest
  // access check has already looked inside each VM (guest tools, drivers, encryption, free space).
  {
    id: "guest-access",
    title: "Guest access",
    breadcrumb: "Guest access",
    description: "SwiftIQ detects each VM's operating system, asks for the matching login, and opens RDP or SSH to match.",
  },
  {
    id: "readiness",
    title: "Readiness",
    breadcrumb: "Readiness",
    description: "Every check, run with the guest login and the choices you made. Fix a blocker and SwiftIQ re-checks it.",
  },
  {
    id: "scripts",
    title: "Scripts",
    breadcrumb: "Scripts",
    description: "Optional scripts to run inside each virtual machine after it moves.",
  },
  {
    id: "schedule",
    title: "Schedule",
    breadcrumb: "Schedule",
    description: "When syncs run on their own.",
  },
  {
    id: "execute",
    title: "Run options",
    breadcrumb: "Run options",
    description: "What happens when you finish.",
  },
  {
    id: "summary",
    title: "Review",
    breadcrumb: "Review",
    description: "Check everything before you create it.",
  },
];

/**
 * Simple mode: the same steps in 5 pages. Each page shows its sections (advanced steps) one under
 * the other; the steps left out (source options, storage, scripts, schedule) keep their defaults.
 * Advanced mode shows `wizardPages` as they are.
 */
export const simpleWizardPages: WizardPage[] = [
  {
    id: "simple-what",
    title: "What to move",
    breadcrumb: "What to move",
    description: "How the virtual machines move, where they run today, and which ones.",
    sections: ["type", "source", "vms"],
  },
  {
    id: "simple-where",
    title: "Where to",
    breadcrumb: "Where to",
    description: "The target cloud, the size and placement of the new virtual machines, and their networks.",
    sections: ["target", "dest-options", "prerequisites", "networks"],
  },
  wizardPages.find(p => p.id === "guest-access")!,
  wizardPages.find(p => p.id === "readiness")!,
  {
    id: "simple-review",
    title: "Review and start",
    breadcrumb: "Review and start",
    description: "Check everything, choose what happens when you finish, and start.",
    sections: ["summary", "execute"],
  },
];

/** True when the page is the given step, or a simple-mode page that shows it. */
export const pageHas = (page: WizardPage | null | undefined, id: string): boolean =>
  Boolean(page && (page.id === id || (page.sections && page.sections.includes(id))));

